import React from "react";
import { Session } from "core/services/authserver";
import { getLoggedIn } from "core/services/authserver/user";
import { Project } from "core/models";
import * as localStorageKeys from "core/constants/localStorageKeys";
import { getProject } from "core/services";

type SessionContextType = {
    session: Session | null;
    selectedProject: Project | null;
    userHasMultipleProjects: boolean;
    userRoles: string[];
    verified: boolean;
    error: string | null;
    setSession(session: Session | null): void;
    setSelectedProject(project: Project | null): void;
    setUserHasMultipleProjects(userHadMultipleProjects: boolean): void;
    setVerified(verified: boolean): void;
    setError(error: string | null): void;
};

const SessionContext = React.createContext<SessionContextType | null>(null);

const useSessionContext = (): SessionContextType => {
    const context = React.useContext(SessionContext);
    if (!context) {
        throw Error("Unable to create session context");
    }
    return context;
};

const getSessionFromStorage = (): Session | null => {
    const sessionString = localStorage.getItem(
        localStorageKeys.SESSION_STORAGE_KEY,
    );
    if (!sessionString) {
        return null;
    }
    try {
        const credentials = JSON.parse(sessionString) as Session;
        return credentials;
    } catch {
        // In case we receive an error, clear the storage and return null
        // as the storage got corrupted somehow ( inspect element script kiddy prolly :| )
        localStorage.removeItem(localStorageKeys.SESSION_STORAGE_KEY);
        return null;
    }
};

// Project
const saveSelectedProject = (project: Project | null) => {
    localStorage.setItem(
        localStorageKeys.SESSION_SELECTED_PROJECT,
        JSON.stringify(project),
    );
};
const getSavedSelectedProject = (): Project | null => {
    const selectedProject = localStorage.getItem(
        localStorageKeys.SESSION_SELECTED_PROJECT,
    );
    if (!selectedProject) {
        return null;
    }
    return JSON.parse(selectedProject);
};
// userHasMultipleProjects
const saveUserHasMultipleProjects = (hasMultipleProjects: boolean) => {
    localStorage.setItem(
        localStorageKeys.SESSION_SELECTED_USER_PROJECTS,
        `${hasMultipleProjects}`,
    );
};
const getSavedUserHasMultipleProjects = (): boolean => {
    const userHasMultipleProjects = localStorage.getItem(
        localStorageKeys.SESSION_SELECTED_USER_PROJECTS,
    );
    return userHasMultipleProjects === "true";
};

// Verified
const getVerified = (): boolean => {
    const isVerified = localStorage.getItem(
        localStorageKeys.SESSION_SELECTED_VERIFIED,
    );
    return isVerified === "true";
};
const saveVerified = (isVerified: boolean) => {
    localStorage.setItem(
        localStorageKeys.SESSION_SELECTED_VERIFIED,
        JSON.stringify(isVerified),
    );
};

type Props = {
    children: React.ReactNode;
};

const SessionContextProvider: React.FC<Props> = ({ children }) => {
    const [session, setSession] = React.useState<Session | null>(
        getSessionFromStorage(),
    );
    const [selectedProject, setSelectedProject] =
        React.useState<Project | null>(getSavedSelectedProject());
    const [error, setError] = React.useState<string | null>(null);
    const [userHasMultipleProjects, setUserHasMultipleProjects] =
        React.useState<boolean>(getSavedUserHasMultipleProjects);
    const [userRoles, setUserRoles] = React.useState<string[]>([]);
    const [verified, setVerified] = React.useState<boolean>(getVerified);

    const gatherUserRoles = async () => {
        if (!session) {
            return;
        }
        const loggedinUser = await getLoggedIn(session.token);
        if (loggedinUser.error || !loggedinUser.value) {
            throw new Error("could not get loggedin user info");
        }
        let totalRoles = loggedinUser.value.roles;

        if (selectedProject) {
            totalRoles = totalRoles.concat(selectedProject.roles);
        }
        setUserRoles(totalRoles);
    };

    const refreshProjectData = async (): Promise<void> => {
        if (!selectedProject || !session?.token) {
            return;
        }
        const result = await getProject(session.token, selectedProject.id);
        if (result.error || !result.value) {
            throw new Error("Error while refreshing project data");
        }
        setSelectedProject(result.value);
        saveSelectedProject(result.value);
    };

    React.useEffect(() => {
        if (session && verified) {
            gatherUserRoles();
        }
    }, [session, selectedProject]);
    React.useEffect(() => {
        if (session && verified) {
            refreshProjectData();
        }
    }, [session]);

    const context: SessionContextType = {
        session,
        selectedProject,
        userHasMultipleProjects,
        userRoles,
        verified,
        error,
        /**
         * Set the session credentials. Calling this function
         * will update the storage of the credentials.
         * Set to null if you want to 'simulate' a logout where the credentials
         * are removed from the backing storage and memory
         */
        // eslint-disable-next-line @typescript-eslint/no-shadow
        setSession: (session: Session) => {
            // Effectively a local logout
            if (!session) {
                localStorage.clear();
                setSession(null);
                setSelectedProject(null);
                setUserHasMultipleProjects(false);
                setUserRoles([]);
                setVerified(false);
                return;
            }

            // If we have a credentials save the object to local storage
            localStorage.setItem(
                localStorageKeys.SESSION_STORAGE_KEY,
                JSON.stringify(session),
            );
            setSession(session);
        },
        setSelectedProject: (project: Project | null) => {
            setSelectedProject(project);
            saveSelectedProject(project);
        },
        setUserHasMultipleProjects: (hasMultipleProjects: boolean) => {
            setUserHasMultipleProjects(hasMultipleProjects);
            saveUserHasMultipleProjects(hasMultipleProjects);
        },
        setVerified: (isVerified: boolean) => {
            setVerified(isVerified);
            saveVerified(isVerified);
        },
        setError: (errorMsg: string | null) => {
            setError(errorMsg);
        },
    };

    return (
        <SessionContext.Provider value={context}>
            {children}
        </SessionContext.Provider>
    );
};

export default SessionContext;
export { SessionContextProvider, useSessionContext };
